import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { useApi } from "../utils/Api";
import { usePersistentForm } from "../hooks/usePersistentForm";
import ErrorModal from "../components/ErrorModal";
import { useErrorModal } from "../hooks/useErrorModal";
import SEO from "../components/SEO";

const money = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(n || 0));

const getTodayUK = () => {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  return formatter.format(new Date());
};

const FORM_DEFAULTS = {
  fullName: "",
  email: "",
  phone: "",
  eventType: "Wedding",
  eventDate: "",
  location: "",
  startTime: "",
  coverageNotes: "",
};

export default function EventPackages() {
  const { apiFetch } = useApi();
  const formRef = useRef(null);
  const { errorMessage, isErrorOpen, showError, closeError } = useErrorModal();

  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [packagesError, setPackagesError] = useState("");
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const { form, updateField } = usePersistentForm({
    scope: "public-site",
    formId: "wedding-package-booking-details",
    version: "v1",
    initialValues: FORM_DEFAULTS,
    persistFields: ["fullName", "email", "phone"],
    ttlMs: 1000 * 60 * 60 * 24 * 45,
    debounceMs: 300,
  });

  useEffect(() => {
    const resetSubmittingState = () => {
      setSubmitting(false);
    };

    const handlePageShow = () => {
      resetSubmittingState();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        resetSubmittingState();
      }
    };

    window.addEventListener("pageshow", handlePageShow);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const minUkDate = useMemo(() => getTodayUK(), []);

  const ensureCoverageText = (value) => {
    if (!value) return "";

    const original = String(value).trim();
    const lower = original.toLowerCase();

    if (lower.includes("coverage")) return original;

    return `${original} coverage`;
  };

  const loadPackages = useCallback(async () => {
    try {
      setLoadingPackages(true);
      setPackagesError("");

      const res = await apiFetch("get_wedding_packages");

      if (!res?.success) {
        throw new Error(res?.message || "Failed to load wedding packages.");
      }

      const mappedPackages = (Array.isArray(res.data) ? res.data : []).map(
        (item) => ({
          id: Number(item.id),
          name: item.name,
          priceFrom: Number(item.price),
          duration: ensureCoverageText(item.coverage),
          highlights: Array.isArray(item.features) ? item.features : [],
        }),
      );

      setPackages(mappedPackages);
      setSelectedPackageId((prev) =>
        prev && mappedPackages.some((pkg) => pkg.id === Number(prev))
          ? Number(prev)
          : mappedPackages.length
            ? mappedPackages[0].id
            : null,
      );
    } catch (err) {
      setPackagesError(err.message || "Unable to load wedding packages.");
      setPackages([]);
      setSelectedPackageId(null);
    } finally {
      setLoadingPackages(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  const selectedPackage = useMemo(() => {
    return (
      packages.find((p) => Number(p.id) === Number(selectedPackageId)) ||
      packages[0] ||
      null
    );
  }, [packages, selectedPackageId]);

  const onChange = (key) => (e) => {
    updateField(key, e.target.value);

    if (isErrorOpen) {
      closeError();
    }
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onSelectPackage = (id) => {
    setSelectedPackageId(id);

    if (isErrorOpen) {
      closeError();
    }

    requestAnimationFrame(scrollToForm);
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!selectedPackage) {
      showError("Please select a package.");
      return;
    }

    if (!form.fullName.trim()) {
      showError("Please enter your full name.");
      return;
    }

    if (!form.email.trim()) {
      showError("Please enter your email address.");
      return;
    }

    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
    if (!emailOk) {
      showError("Please enter a valid email address.");
      return;
    }

    if (!form.phone.trim()) {
      showError("Please enter your phone number.");
      return;
    }

    if (!form.eventDate) {
      showError("Please choose an event date.");
      return;
    }

    if (!form.location.trim()) {
      showError("Please enter the event location.");
      return;
    }

    if (!form.startTime.trim()) {
      showError("Please enter the event start time.");
      return;
    }

    if (submitting) return;

    try {
      setSubmitting(true);
      closeError();

      const payload = {
        packageId: selectedPackage.id,
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        eventType: form.eventType.trim(),
        eventDate: form.eventDate,
        location: form.location.trim(),
        startTime: form.startTime.trim(),
        coverageNotes: form.coverageNotes.trim(),
      };

      const res = await apiFetch("create_event_checkout_session", payload);

      if (!res?.success) {
        throw new Error(res?.message || "Unable to start payment.");
      }

      if (!res?.checkoutUrl) {
        throw new Error("Stripe checkout link was not returned.");
      }

      window.location.href = res.checkoutUrl;
    } catch (err) {
      showError(err.message || "Unable to start payment.");
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Event Photography Packages | Kinews Studio Limited"
        description="Explore our professional event photography packages for weddings and special occasions. Capture every moment with Kinews Studio Limited."
        url={window.location.href}
      />
      <main className="event-packages-page min-h-screen bg-[#0b0b0c] text-white">
        <style>{`
          input[type="date"]::-webkit-calendar-picker-indicator,
          input[type="time"]::-webkit-calendar-picker-indicator {
            opacity: 0;
            position: absolute;
            right: 0;
            width: 100%;
            height: 100%;
            cursor: pointer;
          }

          input[type="date"],
          input[type="time"] {
            color-scheme: dark;
          }
        `}</style>

        <section className="border-b border-white/10 bg-[radial-gradient(circle_at_top,_rgba(236,72,153,0.15),_transparent_35%),linear-gradient(to_bottom,_#111,_#090909)]">
          <div className="mx-auto max-w-7xl px-5 pb-20 pt-32">
            <h1 className="mt-6 text-4xl font-semibold md:text-6xl">
              Wedding Photography Packages
            </h1>

            <p className="mt-4 max-w-xl text-white/70">
              Choose your package, fill in your details, and secure your wedding
              date.
            </p>
          </div>
        </section>

        <section className="bg-[#0f1012]">
          <div className="mx-auto max-w-7xl px-5 py-16">
            <h2 className="text-3xl font-semibold">Choose a package</h2>

            {loadingPackages ? (
              <div className="mt-8 rounded-3xl border border-white/10 bg-white/[0.03] p-8 text-white/70">
                Loading wedding packages...
              </div>
            ) : packagesError ? (
              <div className="mt-8 rounded-3xl border border-red-500/20 bg-red-500/10 p-8 text-red-200">
                {packagesError}
              </div>
            ) : packages.length === 0 ? (
              <div className="mt-8 rounded-3xl border border-white/10 bg-white/[0.03] p-8 text-white/70">
                No wedding packages are available right now.
              </div>
            ) : (
              <div className="mt-8 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
                {packages.map((p) => {
                  const active = Number(p.id) === Number(selectedPackageId);

                  return (
                    <div
                      key={p.id}
                      className={[
                        "flex h-full flex-col rounded-3xl border p-6 transition",
                        active
                          ? "border-pink-500 bg-gradient-to-b from-pink-500/20 to-white/5 shadow-lg"
                          : "border-white/10 bg-white/[0.03] hover:-translate-y-1 hover:border-white/20",
                      ].join(" ")}
                    >
                      <div className="flex h-full flex-col">
                        <div>
                          <div className="flex items-start justify-between">
                            <h3 className="text-xl font-semibold">{p.name}</h3>
                          </div>

                          <p className="mt-3 text-sm text-white/70">
                            {p.duration}
                          </p>

                          <p className="mt-4 text-3xl font-bold text-pink-400">
                            {money(p.priceFrom)}
                          </p>

                          <ul className="mb-6 mt-5 space-y-2 text-sm text-white/70">
                            {p.highlights.map((x, index) => (
                              <li
                                key={`${p.id}-${index}-${x}`}
                                className="flex gap-2"
                              >
                                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-pink-400" />
                                {x}
                              </li>
                            ))}
                          </ul>
                        </div>

                        <button
                          type="button"
                          onClick={() => onSelectPackage(p.id)}
                          className="mt-auto w-full rounded-xl bg-pink-500 py-3 font-semibold transition hover:bg-pink-600"
                        >
                          {active ? "Selected" : "Select"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        <section className="border-y border-white/10 bg-[#0b0b0c]">
          <div className="mx-auto max-w-5xl px-5 py-10 text-center">
            <p className="text-lg text-white/80">
              All digital images are high resolution with print release licence
            </p>
          </div>
        </section>

        <section ref={formRef} className="bg-[#0f1012]">
          <div className="mx-auto max-w-7xl px-5 py-16">
            <h2 className="text-3xl font-semibold">Book your package</h2>

            <div className="mt-8 grid gap-8 lg:grid-cols-12">
              <form
                id="wedding-package-form"
                noValidate
                onSubmit={onSubmit}
                className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 lg:col-span-7"
              >
                <div className="grid gap-4 md:grid-cols-3">
                  <input
                    placeholder="Full name"
                    className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-white outline-none focus:border-pink-500"
                    value={form.fullName}
                    onChange={onChange("fullName")}
                    required
                    autoComplete="name"
                  />
                  <input
                    placeholder="Email"
                    type="email"
                    className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-white outline-none focus:border-pink-500"
                    value={form.email}
                    onChange={onChange("email")}
                    required
                    autoComplete="email"
                  />
                  <input
                    placeholder="Phone"
                    className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-white outline-none focus:border-pink-500"
                    value={form.phone}
                    onChange={onChange("phone")}
                    required
                    autoComplete="tel"
                  />
                </div>

                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <div className="relative">
                    <input
                      type="date"
                      min={minUkDate}
                      className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 pr-10 text-white outline-none focus:border-pink-500"
                      value={form.eventDate}
                      onChange={onChange("eventDate")}
                      required
                    />

                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-white/80"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </span>
                  </div>

                  <input
                    placeholder="Location"
                    className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-white outline-none focus:border-pink-500"
                    value={form.location}
                    onChange={onChange("location")}
                    required
                  />

                  <input
                    placeholder="Event type"
                    className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-white outline-none focus:border-pink-500"
                    value={form.eventType}
                    onChange={onChange("eventType")}
                    required
                  />

                  <div className="relative">
                    <input
                      type="time"
                      className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 pr-10 text-white outline-none focus:border-pink-500"
                      value={form.startTime}
                      onChange={onChange("startTime")}
                      required
                    />

                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-white/80"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </span>
                  </div>
                </div>

                <textarea
                  className="mt-4 w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-white outline-none focus:border-pink-500"
                  rows={4}
                  placeholder="Additional notes"
                  value={form.coverageNotes}
                  onChange={onChange("coverageNotes")}
                />
              </form>

              <aside className="lg:col-span-5">
                <div className="sticky top-6 rounded-3xl border border-pink-500/20 bg-gradient-to-b from-[#181116] to-[#0d0b0d] p-7 shadow-[0_20px_60px_rgba(0,0,0,0.35)]">
                  <p className="text-sm uppercase tracking-[0.25em] text-pink-400/80">
                    Checkout
                  </p>
                  <h3 className="mt-2 text-2xl font-semibold">Order Summary</h3>

                  {!selectedPackage ? (
                    <div className="mt-8 text-sm text-white/70">
                      No package selected.
                    </div>
                  ) : (
                    <div className="mt-8 space-y-4 text-sm text-white/75">
                      <div className="flex justify-between gap-4 border-b border-white/10 pb-3">
                        <span>Package</span>
                        <span className="text-right font-medium text-white">
                          {selectedPackage.name}
                        </span>
                      </div>

                      <div className="flex justify-between gap-4 border-b border-white/10 pb-3">
                        <span>Coverage</span>
                        <span className="text-right font-medium text-white">
                          {selectedPackage.duration}
                        </span>
                      </div>

                      <div className="border-b border-white/10 pb-3">
                        <span className="mb-3 block">Package Features</span>

                        {selectedPackage.highlights?.length ? (
                          <ul className="space-y-2">
                            {selectedPackage.highlights.map(
                              (feature, index) => (
                                <li
                                  key={`summary-feature-${selectedPackage.id}-${index}`}
                                  className="flex items-start gap-2 text-white/85"
                                >
                                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-pink-400" />
                                  <span>{feature}</span>
                                </li>
                              ),
                            )}
                          </ul>
                        ) : (
                          <span className="text-white/60">
                            No package features listed.
                          </span>
                        )}
                      </div>

                      <div className="mt-6 rounded-2xl bg-white/5 px-4 py-4">
                        <div className="flex items-center justify-between">
                          <span className="text-base font-semibold text-white">
                            Total
                          </span>
                          <span className="text-2xl font-semibold text-pink-400">
                            {money(selectedPackage.priceFrom)}
                          </span>
                        </div>
                      </div>

                      <button
                        type="submit"
                        form="wedding-package-form"
                        disabled={submitting || loadingPackages}
                        className={`mt-6 w-full rounded-xl py-4 font-semibold transition ${
                          submitting || loadingPackages
                            ? "cursor-not-allowed bg-pink-500/40 text-white/70"
                            : "bg-pink-500 text-white hover:bg-pink-600"
                        }`}
                      >
                        {submitting ? "Redirecting..." : "Continue to Payment"}
                      </button>
                    </div>
                  )}
                </div>
              </aside>
            </div>
          </div>
        </section>

        <section className="bg-black text-white">
          <div className="mx-auto max-w-7xl px-5 py-14">
            <div className="flex flex-col items-center justify-between gap-6 rounded-3xl border border-white/10 p-8 md:flex-row">
              <div>
                <h2 className="text-2xl font-semibold">
                  Need a studio session instead?
                </h2>
                <p className="mt-2 text-white/70">
                  Book portraits and indoor shoots easily.
                </p>
              </div>

              <Link
                to="/book"
                className="rounded-xl bg-pink-500 px-6 py-3 font-semibold text-white"
              >
                Go to Bookings
              </Link>
            </div>
          </div>
        </section>
      </main>

      <ErrorModal
        open={isErrorOpen}
        message={errorMessage}
        onClose={closeError}
        autoCloseMs={4000}
      />
    </>
  );
}
